import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, Check, Sparkles, X } from "lucide-react";

export default function QuickStartGuide({ open, onOpenChange, autoMarkSeen = true }) {
    const [steps, setSteps] = useState([]);
    const [index, setIndex] = useState(0);
    const { setUser, user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!open) return;
        if (steps.length === 0) {
            api.get("/assistant/quickstart").then(({ data }) => setSteps(data.steps || []));
        }
    }, [open, steps.length]);

    const markSeen = async () => {
        if (!autoMarkSeen) return;
        try {
            await api.post("/auth/quickstart-seen");
            if (setUser && user) setUser({ ...user, seen_quickstart: true });
        } catch {}
    };

    const close = async () => {
        await markSeen();
        onOpenChange(false);
        setIndex(0);
    };

    const next = () => {
        if (index < steps.length - 1) setIndex(index + 1);
        else close();
    };

    const jumpTo = (link) => {
        if (link) navigate(link);
        close();
    };

    if (steps.length === 0) return null;
    const step = steps[index];

    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogContent className="max-w-2xl p-0 overflow-hidden">
                <div className="relative bg-deep-ocean text-white p-8">
                    <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-secondary/30 blur-3xl" />
                    <div className="absolute -left-10 -bottom-10 h-32 w-32 rounded-full bg-coral/20 blur-3xl" />
                    <div className="relative">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-secondary-light font-semibold">
                            <Sparkles className="h-3.5 w-3.5" /> Step {index + 1} of {steps.length}
                        </div>
                        <h2 className="mt-3 font-heading text-3xl sm:text-4xl tracking-tight">{step.title}</h2>
                        <p className="mt-2 text-white/80">{step.summary}</p>
                    </div>
                </div>

                <div className="px-8 py-7">
                    <p className="text-[15px] text-ocean-700/90 leading-relaxed">{step.body}</p>

                    <div className="mt-7 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-1.5" data-testid="quickstart-dots">
                            {steps.map((s, i) => (
                                <button
                                    key={s.id}
                                    aria-label={`Step ${i + 1}`}
                                    onClick={() => setIndex(i)}
                                    className={`h-1.5 rounded-full transition-all ${
                                        i === index ? "bg-secondary w-6" : "bg-sand-300 w-3 hover:bg-sand-400"
                                    }`}
                                />
                            ))}
                        </div>
                        <div className="flex items-center gap-2">
                            {index > 0 && (
                                <Button variant="ghost" onClick={() => setIndex(index - 1)} data-testid="quickstart-back">Back</Button>
                            )}
                            {step.link ? (
                                <Button onClick={() => jumpTo(step.link)} variant="outline" className="rounded-xl border-sand-300" data-testid="quickstart-cta">
                                    {step.cta} <ArrowRight className="h-4 w-4 ml-1.5" />
                                </Button>
                            ) : null}
                            <Button onClick={next} className="bg-ocean-700 hover:bg-ocean-700/90 text-white rounded-xl" data-testid="quickstart-next">
                                {index === steps.length - 1 ? (
                                    <>Finish <Check className="h-4 w-4 ml-1.5" /></>
                                ) : (
                                    <>Next <ArrowRight className="h-4 w-4 ml-1.5" /></>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
